/**
 * NotaEvolucionPanel — Banner de contexto previo + lista de problemas activos
 * con selector de evolución (Mejor / Igual / Peor / Resuelto / Nuevo).
 *
 * Diseño basado en POMR (Problem-Oriented Medical Record) y SOAP.
 */
import React from 'react';
import {
  Box, Typography, Chip, Button, TextField, MenuItem, Tooltip,
} from '@mui/material';
import {
  HistoryEdu as HistoryIcon,
  Medication as MedicationIcon,
  Science as ScienceIcon,
  Info as InfoIcon,
  PlayArrow as ContinuarIcon,
  Tune as AjustarIcon,
  StopCircle as SuspenderIcon,
} from '@mui/icons-material';

const ADHERENCIA_OPCIONES = [
  { key: 'total',   label: 'Total',   color: '#10b981' },
  { key: 'parcial', label: 'Parcial', color: '#f59e0b' },
  { key: 'no',      label: 'No',      color: '#ef4444' },
];

const ACCION_MED_OPCIONES = [
  { key: 'continuar', label: 'Continuar igual', color: '#10b981', bg: '#ecfdf5', borde: '#86efac', Icon: ContinuarIcon },
  { key: 'ajustar',   label: 'Ajustar dosis',   color: '#f59e0b', bg: '#fffbeb', borde: '#fde68a', Icon: AjustarIcon },
  { key: 'suspender', label: 'Suspender',       color: '#ef4444', bg: '#fef2f2', borde: '#fca5a5', Icon: SuspenderIcon },
];

export default function NotaEvolucionPanel({
  contexto,           // { ultima_consulta, problemas_activos, medicamentos_previos, estudios_solicitados_previos }
  evolucion,          // { problemas, adherencia_tratamiento, adherencia_motivo, evolucion_general, conciliacion_medicamentos }
  onChange,
  onAplicarConciliacion,  // callback: añade meds "continuar" y "ajustar" a la receta del padre
  cargandoContexto = false,
}) {
  if (cargandoContexto) {
    return (
      <Box sx={{
        p: 2, borderRadius: 2, background: '#f8fafc',
        border: '1px dashed #cbd5e1', textAlign: 'center',
      }}>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Cargando contexto de la consulta previa...
        </Typography>
      </Box>
    );
  }

  if (!contexto?.ultima_consulta) {
    return (
      <Box sx={{
        p: 2, borderRadius: 2,
        background: '#fff7ed', border: '1px solid #fed7aa',
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <InfoIcon sx={{ color: '#c2410c', fontSize: 20 }} />
        <Typography sx={{ fontSize: 12.5, color: '#9a3412' }}>
          No hay consulta previa registrada para este expediente. Esta nota se tratará como primera atención.
        </Typography>
      </Box>
    );
  }

  const ultima = contexto.ultima_consulta;
  const problemas = contexto.problemas_activos || [];
  const meds = contexto.medicamentos_previos || [];
  const estudios = contexto.estudios_solicitados_previos || [];

  const fechaUltima = ultima.fecha
    ? new Date(ultima.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* ============ BANNER STICKY: contexto de la consulta previa ============ */}
      <Box sx={{
        p: 1.5,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)',
        border: '1px solid #c7d2fe',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: 3, background: 'linear-gradient(180deg, #6366f1, #8b5cf6)',
        }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.7 }}>
          <HistoryIcon sx={{ fontSize: 16, color: '#4f46e5' }} />
          <Typography sx={{
            fontSize: 10, fontWeight: 800, color: '#4f46e5',
            letterSpacing: 0.6, textTransform: 'uppercase',
          }}>
            Contexto · Última consulta — {fechaUltima}
          </Typography>
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1,
          rowGap: 0.5,
        }}>
          {ultima.motivo_consulta && (
            <Box>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Motivo
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: '#1e1b4b', fontWeight: 600 }}>
                {ultima.motivo_consulta}
              </Typography>
            </Box>
          )}
          {ultima.diagnostico_principal && (
            <Box sx={{ gridColumn: { md: '1 / -1' } }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.4 }}>
                Diagnósticos previos
              </Typography>
              {/* Principal destacado */}
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.4,
                px: 0.8, py: 0.4, borderRadius: 1.5, mr: 0.4, mb: 0.3,
                background: '#dcfce7', border: '1px solid #86efac',
              }}>
                <Box sx={{
                  px: 0.5, py: 0.05, borderRadius: 0.7, background: '#10b981', color: '#fff',
                  fontSize: 8.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', lineHeight: 1.4,
                }}>
                  Principal
                </Box>
                <Typography sx={{ fontSize: 11.5, color: '#14532d', fontWeight: 700 }}>
                  {ultima.diagnostico_principal.codigo}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: '#14532d' }}>
                  · {ultima.diagnostico_principal.descripcion}
                </Typography>
              </Box>
              {/* Co-diagnósticos */}
              {Array.isArray(ultima.diagnosticos_secundarios) && ultima.diagnosticos_secundarios.map((d, i) => (
                <Chip
                  key={`${d.codigo}-${i}`}
                  size="small"
                  label={
                    <span>
                      <b>{d.codigo}</b> · {d.descripcion}
                    </span>
                  }
                  sx={{
                    mr: 0.4, mb: 0.3, height: 22,
                    background: '#fff', color: '#3730a3',
                    border: '1px solid #c7d2fe',
                    fontSize: 11,
                    '& .MuiChip-label': { px: 0.8 },
                  }}
                />
              ))}
            </Box>
          )}
          {/* Impresión diagnóstica narrativa (texto libre) */}
          {ultima.impresion_diagnostica && (
            <Box sx={{ gridColumn: { md: '1 / -1' } }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
                Impresión diagnóstica narrativa
              </Typography>
              <Box sx={{
                pl: 1, borderLeft: '3px solid #a5b4fc',
                background: '#fff', borderRadius: '0 6px 6px 0',
                py: 0.5, px: 0.8,
              }}>
                <Typography sx={{
                  fontSize: 11.5, color: '#312e81', fontStyle: 'italic',
                  whiteSpace: 'pre-wrap', lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  "{ultima.impresion_diagnostica}"
                </Typography>
              </Box>
            </Box>
          )}
          {ultima.plan_tratamiento && (
            <Box sx={{ gridColumn: { md: '1 / -1' } }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Plan de tratamiento previo
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#312e81', whiteSpace: 'pre-wrap' }}>
                {ultima.plan_tratamiento}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Medicamentos previos resumidos */}
        {meds.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #c7d2fe' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
              <MedicationIcon sx={{ fontSize: 14, color: '#7c3aed' }} />
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Tratamiento vigente ({meds.length})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
              {meds.slice(0, 6).map((m, i) => (
                <Chip
                  key={i} size="small"
                  label={`${m.nombre}${m.dosis ? ` ${m.dosis}` : ''}${m.frecuencia ? ` · ${m.frecuencia}` : ''}`}
                  sx={{
                    fontSize: 10, height: 20,
                    background: '#fff', color: '#5b21b6',
                    border: '1px solid #d8b4fe',
                  }}
                />
              ))}
              {meds.length > 6 && (
                <Chip size="small" label={`+${meds.length - 6} más`}
                  sx={{ fontSize: 10, height: 20, background: '#ede9fe', color: '#5b21b6' }} />
              )}
            </Box>
          </Box>
        )}

        {/* Estudios previos pendientes a revisar */}
        {estudios.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #c7d2fe' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <ScienceIcon sx={{ fontSize: 14, color: '#0891b2' }} />
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#0891b2', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Estudios pendientes de revisar ({estudios.length})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.5 }}>
              {estudios.map((est, i) => {
                const labelTipo = est.tipo === 'laboratorio' ? '🧪' : '🏥';
                const nombre = est.tipo === 'laboratorio'
                  ? (est.contenido?.estudios?.[0]?.nombre || `Lab ${est.id}`) +
                    (est.contenido?.estudios?.length > 1 ? ` +${est.contenido.estudios.length - 1}` : '')
                  : (est.contenido?.especialidad || `Interconsulta`);
                return (
                  <Chip
                    key={i} size="small"
                    label={`${labelTipo} ${nombre}`}
                    sx={{
                      fontSize: 10, height: 20,
                      background: '#fff', color: '#0c4a6e',
                      border: '1px solid #67e8f9',
                    }}
                  />
                );
              })}
            </Box>
            <Typography sx={{ fontSize: 10.5, color: '#075985', fontStyle: 'italic' }}>
              💡 Si el paciente trae los resultados, captúralos en la pestaña <b>Laboratorios / Interconsulta</b> (sección "Resultados de estudios del paciente").
            </Typography>
          </Box>
        )}
      </Box>

      {/* ============ ADHERENCIA AL TRATAMIENTO ============ */}
      {meds.length > 0 && (
        <Box sx={{
          p: 1.5, borderRadius: 2,
          background: '#fff', border: '1px solid #e2e8f0',
        }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 700, color: '#0f172a',
            textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.7,
          }}>
            Adherencia al tratamiento
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mb: 1 }}>
            {ADHERENCIA_OPCIONES.map(op => {
              const seleccionado = evolucion?.adherencia_tratamiento === op.key;
              return (
                <Button
                  key={op.key}
                  size="small"
                  variant={seleccionado ? 'contained' : 'outlined'}
                  onClick={() => onChange({ ...evolucion, adherencia_tratamiento: seleccionado ? null : op.key })}
                  sx={{
                    textTransform: 'none', fontWeight: 700, fontSize: 11,
                    minWidth: 80, borderRadius: 2,
                    background: seleccionado ? op.color : '#fff',
                    borderColor: op.color,
                    color: seleccionado ? '#fff' : op.color,
                    '&:hover': {
                      background: seleccionado ? op.color : `${op.color}15`,
                      borderColor: op.color,
                    },
                  }}
                >
                  {op.label}
                </Button>
              );
            })}
          </Box>
          {evolucion?.adherencia_tratamiento && evolucion.adherencia_tratamiento !== 'total' && (
            <TextField
              fullWidth size="small"
              placeholder="¿Por qué no se adhirió completamente? (efectos adversos, costo, olvido, etc.)"
              value={evolucion?.adherencia_motivo || ''}
              onChange={(e) => onChange({ ...evolucion, adherencia_motivo: e.target.value })}
              sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 12 } }}
            />
          )}
        </Box>
      )}

      {/* ============ EVOLUCIÓN GENERAL (¿cómo se ha sentido?) ============ */}
      <Box sx={{
        p: 1.5, borderRadius: 2,
        background: '#fff', border: '1px solid #e2e8f0',
      }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 700, color: '#0f172a',
          textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.7,
        }}>
          ¿Cómo se ha sentido desde la última consulta?
        </Typography>
        <TextField
          fullWidth multiline minRows={2} maxRows={5} size="small"
          placeholder="Resumen subjetivo de la evolución del paciente: cambios percibidos, síntomas nuevos, efectos del tratamiento, etc."
          value={evolucion?.evolucion_general || ''}
          onChange={(e) => onChange({ ...evolucion, evolucion_general: e.target.value })}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 12.5 } }}
        />
      </Box>

      {/* ============ PROBLEMAS ACTIVOS — solo referencia visual ============ */}
      {problemas.length > 0 && (
        <Box sx={{
          p: 1.5, borderRadius: 2,
          background: '#fff', border: '1px solid #e2e8f0',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
            <Typography sx={{
              fontSize: 11, fontWeight: 700, color: '#0f172a',
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              Problemas activos del expediente
            </Typography>
            <Chip
              size="small" label={problemas.length}
              sx={{ height: 18, fontSize: 10, background: '#eef2ff', color: '#4338ca', fontWeight: 700 }}
            />
            <Typography sx={{ ml: 'auto', fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
              Referencia · documentá la evolución arriba
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {problemas.map(p => {
              const esPrincipal = p.tipo_diagnostico === 'principal';
              return (
                <Chip
                  key={p.codigo_cie10}
                  size="small"
                  label={
                    <span>
                      <b style={{ color: esPrincipal ? '#15803d' : '#3730a3' }}>{p.codigo_cie10}</b>
                      {' · '}{p.descripcion}
                      {esPrincipal && (
                        <span style={{
                          marginLeft: 6, fontSize: 9, fontWeight: 800,
                          opacity: 0.85, letterSpacing: 0.4,
                        }}>
                          · PRINCIPAL
                        </span>
                      )}
                    </span>
                  }
                  sx={{
                    height: 24,
                    fontSize: 11.5,
                    fontWeight: 600,
                    background: esPrincipal ? '#dcfce7' : '#f1f5f9',
                    color: esPrincipal ? '#15803d' : '#334155',
                    border: esPrincipal ? '1px solid #86efac' : '1px solid #e2e8f0',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* ============ CONCILIACIÓN DE MEDICAMENTOS ============ */}
      {meds.length > 0 && (
        <ConciliacionMedicamentos
          medsPrevios={meds}
          conciliacion={evolucion?.conciliacion_medicamentos || {}}
          onChange={(nuevaConciliacion) => onChange({
            ...evolucion,
            conciliacion_medicamentos: nuevaConciliacion,
          })}
          onAplicar={onAplicarConciliacion}
        />
      )}
    </Box>
  );
}

function ConciliacionMedicamentos({ medsPrevios, conciliacion, onChange, onAplicar }) {
  // conciliacion: { "<index_med>": { accion: "continuar|ajustar|suspender", motivo: "..." } }
  const setAccion = (idx, accion) => {
    const actual = conciliacion[idx] || {};
    if (actual.accion === accion) {
      const { [idx]: _, ...resto } = conciliacion;
      onChange(resto);
    } else {
      onChange({ ...conciliacion, [idx]: { ...actual, accion } });
    }
  };
  const setMotivo = (idx, motivo) => {
    const actual = conciliacion[idx] || {};
    onChange({ ...conciliacion, [idx]: { ...actual, motivo } });
  };

  const completos = medsPrevios.filter((_, i) => conciliacion[i]?.accion).length;
  const total = medsPrevios.length;
  const todasDecididas = completos === total;

  return (
    <Box sx={{
      p: 1.5, borderRadius: 2,
      background: '#fff', border: '1px solid #e2e8f0',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1 }}>
        <MedicationIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
        <Typography sx={{
          fontSize: 11, fontWeight: 700, color: '#0f172a',
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          Conciliación de medicamentos
        </Typography>
        <Tooltip title="Por cada medicamento del tratamiento previo, decidí si lo continuás, ajustás la dosis o lo suspendés. Estándar de seguridad clínica (Joint Commission, OMS).">
          <InfoIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
        </Tooltip>
        <Chip
          size="small" label={`${completos}/${total}`}
          sx={{
            ml: 'auto', height: 18, fontSize: 10, fontWeight: 700,
            background: todasDecididas ? '#dcfce7' : '#fef3c7',
            color: todasDecididas ? '#15803d' : '#a16207',
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
        {medsPrevios.map((med, idx) => {
          const decision = conciliacion[idx] || {};
          const op = ACCION_MED_OPCIONES.find(o => o.key === decision.accion);
          return (
            <Box key={idx} sx={{
              p: 0.9,
              borderRadius: 1.5,
              background: op ? op.bg : '#f8fafc',
              border: `1px solid ${op ? op.borde : '#e2e8f0'}`,
              borderLeft: `3px solid ${op ? op.color : '#cbd5e1'}`,
              opacity: decision.accion === 'suspender' ? 0.7 : 1,
              transition: 'all 0.2s',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.7 }}>
                <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: 12.5, fontWeight: 700, color: '#0f172a',
                    textDecoration: decision.accion === 'suspender' ? 'line-through' : 'none',
                  }}>
                    {med.nombre}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    {[
                      med.dosis, med.via && `vía ${med.via}`,
                      med.frecuencia, med.duracion && `× ${med.duracion}`,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                  {ACCION_MED_OPCIONES.map(opc => {
                    const activo = decision.accion === opc.key;
                    const Ico = opc.Icon;
                    return (
                      <Button
                        key={opc.key}
                        size="small"
                        startIcon={<Ico sx={{ fontSize: 13 }} />}
                        onClick={() => setAccion(idx, opc.key)}
                        sx={{
                          textTransform: 'none', fontWeight: 700, fontSize: 10.5,
                          minWidth: 0, px: 0.9, py: 0.2, borderRadius: 1.5,
                          background: activo ? opc.color : '#fff',
                          color: activo ? '#fff' : opc.color,
                          border: `1px solid ${activo ? opc.color : opc.borde}`,
                          '&:hover': {
                            background: activo ? opc.color : `${opc.color}10`,
                            borderColor: opc.color,
                          },
                          '& .MuiButton-startIcon': { marginRight: 0.4 },
                        }}
                      >
                        {opc.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
              {decision.accion && decision.accion !== 'continuar' && (
                <TextField
                  fullWidth size="small"
                  placeholder={
                    decision.accion === 'ajustar'
                      ? 'Detallá el ajuste (nueva dosis, frecuencia o vía)...'
                      : 'Motivo de suspensión (efecto adverso, resolución del problema, etc.)...'
                  }
                  value={decision.motivo || ''}
                  onChange={(e) => setMotivo(idx, e.target.value)}
                  sx={{
                    mt: 0.7,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1, fontSize: 11.5, background: '#fff',
                      '& fieldset': { borderColor: op?.borde || '#e2e8f0' },
                    },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Botón aplicar al plan */}
      {onAplicar && completos > 0 && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<MedicationIcon sx={{ fontSize: 15 }} />}
            onClick={() => onAplicar(medsPrevios, conciliacion)}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 11,
              borderRadius: 2, px: 1.5,
              background: '#7c3aed',
              boxShadow: '0 4px 10px rgba(124, 58, 237, 0.3)',
              '&:hover': { background: '#6d28d9' },
            }}
          >
            Aplicar al plan de tratamiento
          </Button>
        </Box>
      )}

      {!todasDecididas && (
        <Typography sx={{ fontSize: 10.5, color: '#a16207', mt: 0.7, fontStyle: 'italic' }}>
          ⚠ Conciliación incompleta — faltan {total - completos} medicamento{total - completos !== 1 ? 's' : ''} por revisar.
        </Typography>
      )}
    </Box>
  );
}
